import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Bell,
  BookOpen,
  Building2,
  CheckSquare,
  FileText,
  FolderKanban,
  Home,
  KeyRound,
  LayoutGrid,
  Repeat,
  Search,
  Settings,
  Tags,
  UsersRound,
} from 'lucide-react';
import type { User } from '@/types';

export type UserRole = User['role'];

export interface NavigationAccess {
  /** Vendor access is explicitly granted by the server, independently of role. */
  vendorAccess?: boolean;
}

const VENDOR_ROUTE_KEYS: readonly AppRouteKey[] = ['vendors', 'properties', 'serviceTypes'];
export type NavigationGroup =
  'My Work' | 'Projects' | 'Vendors & properties' | 'Workspace' | 'Automation' | 'Administration';

export type AppRouteKey =
  | 'home'
  | 'myTasks'
  | 'projects'
  | 'project'
  | 'projectAutomation'
  | 'projectForms'
  | 'workgroups'
  | 'search'
  | 'inbox'
  | 'settings'
  | 'activity'
  | 'recurring'
  | 'credentials'
  | 'vendors'
  | 'properties'
  | 'serviceTypes';

export interface AppRouteDefinition {
  key: AppRouteKey;
  path: string;
  label: string;
  mobileLabel?: string;
  mobileIcon?: LucideIcon;
  icon: LucideIcon;
  roles: readonly UserRole[];
  group?: NavigationGroup;
  parent?: AppRouteKey;
  sidebar?: boolean;
  mobile?: readonly UserRole[];
  /** Include this destination in the mobile More workspace hub. */
  mobileMore?: readonly UserRole[];
  searchKeywords?: readonly string[];
  /** Nested pages share this destination's access and navigation context. */
  includesChildren?: boolean;
}

const ALL_AUTHENTICATED = ['admin', 'member', 'guest'] as const;
const WORKSPACE_MEMBERS = ['admin', 'member'] as const;
const PROJECT_MEMBERS = WORKSPACE_MEMBERS;
const PROJECT_COLLABORATORS = ALL_AUTHENTICATED;

/**
 * The single source of truth for authenticated route access, navigation labels,
 * grouping, breadcrumbs, and mobile destinations.
 */
export const APP_ROUTES: Record<AppRouteKey, AppRouteDefinition> = {
  home: {
    key: 'home',
    path: '/',
    label: 'Home',
    mobileLabel: 'Today',
    icon: Home,
    roles: ALL_AUTHENTICATED,
    group: 'My Work',
    sidebar: true,
    mobile: ALL_AUTHENTICATED,
    searchKeywords: ['dashboard', 'overview'],
  },
  myTasks: {
    key: 'myTasks',
    path: '/my-tasks',
    label: 'My Tasks',
    mobileLabel: 'Tasks',
    icon: CheckSquare,
    roles: PROJECT_MEMBERS,
    group: 'My Work',
    sidebar: true,
    mobile: PROJECT_MEMBERS,
    searchKeywords: ['assignments', 'work'],
  },
  activity: {
    key: 'activity',
    path: '/activity-feed',
    label: 'Activity',
    icon: Activity,
    roles: PROJECT_MEMBERS,
    group: 'My Work',
    sidebar: true,
    mobileMore: PROJECT_MEMBERS,
    searchKeywords: ['feed', 'updates'],
  },
  projects: {
    key: 'projects',
    path: '/projects',
    label: 'All Projects',
    mobileLabel: 'Projects',
    icon: FolderKanban,
    roles: PROJECT_MEMBERS,
    group: 'Projects',
    sidebar: true,
    mobile: PROJECT_MEMBERS,
    searchKeywords: ['project list'],
  },
  project: {
    key: 'project',
    path: '/projects/:projectId',
    label: 'Project',
    icon: FolderKanban,
    roles: PROJECT_COLLABORATORS,
    parent: 'projects',
  },
  projectAutomation: {
    key: 'projectAutomation',
    path: '/projects/:projectId/automation',
    label: 'Automation',
    icon: Repeat,
    roles: PROJECT_MEMBERS,
    parent: 'project',
  },
  projectForms: {
    key: 'projectForms',
    path: '/projects/:projectId/forms',
    label: 'Forms',
    icon: FileText,
    roles: PROJECT_MEMBERS,
    parent: 'project',
  },
  workgroups: {
    key: 'workgroups',
    path: '/teams',
    label: 'Workgroups',
    icon: UsersRound,
    roles: WORKSPACE_MEMBERS,
    group: 'Workspace',
    sidebar: true,
    mobileMore: WORKSPACE_MEMBERS,
    searchKeywords: ['teams', 'spaces'],
  },
  vendors: {
    key: 'vendors',
    path: '/vendors',
    label: 'Vendors',
    icon: BookOpen,
    roles: WORKSPACE_MEMBERS,
    group: 'Vendors & properties',
    sidebar: true,
    mobileMore: WORKSPACE_MEMBERS,
    includesChildren: true,
    searchKeywords: ['service history', 'invoices', 'payments', 'follow-ups'],
  },
  properties: {
    key: 'properties',
    path: '/properties',
    label: 'Properties',
    icon: Building2,
    roles: WORKSPACE_MEMBERS,
    group: 'Vendors & properties',
    mobileMore: WORKSPACE_MEMBERS,
    parent: 'vendors',
    includesChildren: true,
    searchKeywords: ['addresses', 'locations', 'homes'],
  },
  serviceTypes: {
    key: 'serviceTypes',
    path: '/service-types',
    label: 'Service types',
    icon: Tags,
    roles: WORKSPACE_MEMBERS,
    group: 'Vendors & properties',
    parent: 'vendors',
    includesChildren: true,
    searchKeywords: ['vendor categories'],
  },
  recurring: {
    key: 'recurring',
    path: '/recurring-tasks',
    label: 'Recurring Tasks',
    icon: Repeat,
    roles: PROJECT_MEMBERS,
    group: 'Automation',
    sidebar: true,
    mobileMore: PROJECT_MEMBERS,
  },
  credentials: {
    key: 'credentials',
    path: '/credentials',
    label: 'Credentials',
    icon: KeyRound,
    roles: ['admin'],
    group: 'Administration',
    sidebar: true,
    mobileMore: ['admin'],
    searchKeywords: ['vault', 'passwords', 'account access'],
  },
  settings: {
    key: 'settings',
    path: '/settings',
    label: 'Settings',
    icon: Settings,
    roles: WORKSPACE_MEMBERS,
    group: 'Administration',
    mobile: WORKSPACE_MEMBERS,
    mobileLabel: 'More',
    mobileIcon: LayoutGrid,
  },
  inbox: {
    key: 'inbox',
    path: '/notifications',
    label: 'Inbox',
    icon: Bell,
    roles: WORKSPACE_MEMBERS,
    mobile: WORKSPACE_MEMBERS,
    searchKeywords: ['notifications', 'alerts'],
  },
  search: {
    key: 'search',
    path: '/search',
    label: 'Search',
    icon: Search,
    roles: ALL_AUTHENTICATED,
    group: 'My Work',
    mobile: ['guest'],
    mobileMore: WORKSPACE_MEMBERS,
  },
};

const NAVIGATION_GROUP_ORDER: readonly NavigationGroup[] = [
  'My Work',
  'Projects',
  'Vendors & properties',
  'Workspace',
  'Automation',
  'Administration',
];

export function routeRoles(key: AppRouteKey): readonly UserRole[] {
  return APP_ROUTES[key].roles;
}

export function canAccessRoute(
  key: AppRouteKey,
  role: UserRole | undefined,
  access: NavigationAccess = {},
): boolean {
  return (
    !!role &&
    APP_ROUTES[key].roles.includes(role) &&
    (!VENDOR_ROUTE_KEYS.includes(key) || access.vendorAccess === true)
  );
}

export function getSidebarGroups(role: UserRole | undefined, access: NavigationAccess = {}) {
  return NAVIGATION_GROUP_ORDER.map((group) => ({
    group,
    items: Object.values(APP_ROUTES).filter(
      (route) => route.sidebar && route.group === group && canAccessRoute(route.key, role, access),
    ),
  })).filter(({ items }) => items.length > 0);
}

export function getMobileRoutes(role: UserRole | undefined, access: NavigationAccess = {}) {
  const mobileOrder: readonly AppRouteKey[] =
    role === 'guest' ? ['home', 'search'] : ['home', 'myTasks', 'projects', 'inbox', 'settings'];
  return mobileOrder
    .map((key) => APP_ROUTES[key])
    .filter(
      (route) =>
        role &&
        route.mobile?.includes(role) &&
        !VENDOR_ROUTE_KEYS.includes(route.key) &&
        canAccessRoute(route.key, role, access),
    );
}

/** Secondary mobile destinations, grouped from the same registry as desktop navigation. */
export function getMobileWorkspaceGroups(
  role: UserRole | undefined,
  access: NavigationAccess = {},
) {
  const mobileGroupOrder: readonly NavigationGroup[] = [
    'Vendors & properties',
    ...NAVIGATION_GROUP_ORDER.filter((group) => group !== 'Vendors & properties'),
  ];
  return mobileGroupOrder
    .map((group) => ({
      group,
      items: Object.values(APP_ROUTES).filter(
        (route) =>
          route.group === group &&
          !!role &&
          route.mobileMore?.includes(role) &&
          !VENDOR_ROUTE_KEYS.includes(route.key) &&
          canAccessRoute(route.key, role, access),
      ),
    }))
    .filter(({ items }) => items.length > 0);
}

function pathSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

function routeMatchesPath(routePath: string, pathname: string): boolean {
  if (routePath === '/') return pathname === '/';
  const pattern = pathSegments(routePath);
  const actual = pathSegments(pathname);
  if (pattern.length !== actual.length) return false;
  return pattern.every((segment, index) => segment.startsWith(':') || segment === actual[index]);
}

export function getRouteForPath(pathname: string): AppRouteDefinition | undefined {
  return Object.values(APP_ROUTES)
    .sort((a, b) => pathSegments(b.path).length - pathSegments(a.path).length)
    .find(
      (route) =>
        routeMatchesPath(route.path, pathname) ||
        (route.includesChildren && pathname.startsWith(`${route.path}/`)),
    );
}

export function getParentBreadcrumb(
  pathname: string,
  role?: UserRole,
  access: NavigationAccess = {},
) {
  const route = getRouteForPath(pathname);
  if (!route?.parent || (VENDOR_ROUTE_KEYS.includes(route.key) && !access.vendorAccess))
    return null;
  const parent = APP_ROUTES[route.parent];
  const to =
    !parent.path.includes(':') && (!role || canAccessRoute(parent.key, role, access))
      ? parent.path
      : undefined;
  return { label: parent.label, to };
}
